<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="headline font-weight-bold">{{ $t('golf_caddies') || 'Caddies' }}</span>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" rounded elevation="2" @click="openDialog()">
                        <v-icon left>mdi-plus</v-icon>
                        Nuevo Caddie
                    </v-btn>
                </v-card-title>

                <!-- Estadísticas -->
                <v-row class="mb-4">
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="text-center pa-3">
                            <div class="display-1 font-weight-bold primary--text">{{ caddies.length }}</div>
                            <div class="caption grey--text">Caddies Totales</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="text-center pa-3">
                            <div class="display-1 font-weight-bold green--text">{{ activeCaddies }}</div>
                            <div class="caption grey--text">Activos</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="text-center pa-3">
                            <div class="display-1 font-weight-bold orange--text">{{ totalRoundsPlayed }}</div>
                            <div class="caption grey--text">Total Rondas</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="text-center pa-3">
                            <div class="display-1 font-weight-bold info--text">{{ avgRating }}</div>
                            <div class="caption grey--text">Calificación Promedio</div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Búsqueda -->
                <v-card class="mb-4">
                    <v-card-text class="pa-4">
                        <v-text-field
                            v-model="searchQuery"
                            prepend-inner-icon="mdi-magnify"
                            label="Buscar por nombre o apodo..."
                            outlined
                            dense
                            clearable
                        ></v-text-field>
                    </v-card-text>
                </v-card>

                <!-- Tabla de Caddies -->
                <v-card class="elevation-1">
                    <v-card-subtitle class="pa-4">
                        <v-icon small class="mr-2">mdi-account-tie</v-icon>
                        Padrón de Caddies
                    </v-card-subtitle>
                    <v-data-table
                        :headers="headers"
                        :items="filteredCaddies"
                        :loading="loading"
                        class="elevation-0"
                        :no-data-text="'Sin caddies registrados'">

                        <template v-slot:item.photo="{ item }">
                            <v-avatar size="40" class="my-2" color="primary">
                                <span class="white--text font-weight-bold">{{ getInitials(item.name) }}</span>
                            </v-avatar>
                        </template>

                        <template v-slot:item.name="{ item }">
                            <div class="font-weight-bold">{{ item.name }}</div>
                            <div class="caption grey--text">{{ item.nickname }}</div>
                        </template>

                        <template v-slot:item.caddie_type="{ item }">
                            <v-chip small :color="getTypeColor(item.caddie_type)">
                                {{ getCaddieTypeName(item.caddie_type) }}
                            </v-chip>
                        </template>

                        <template v-slot:item.is_active="{ item }">
                            <v-chip :color="item.is_active ? 'green' : 'grey'" small text-color="white">
                                {{ item.is_active ? 'Activo' : 'Inactivo' }}
                            </v-chip>
                        </template>

                        <template v-slot:item.total_rounds_completed="{ item }">
                            <div class="text-center">
                                <v-chip small color="info" text-color="white">{{ item.total_rounds_completed }}</v-chip>
                            </div>
                        </template>

                        <template v-slot:item.avg_rating_value="{ item }">
                            <div class="d-flex align-center justify-center">
                                <v-icon small color="orange" class="mr-1">mdi-star</v-icon>
                                <strong>{{ parseFloat(item.avg_rating_value).toFixed(1) }}</strong>
                            </div>
                        </template>

                        <template v-slot:item.bonus_eligible="{ item }">
                            <v-tooltip v-if="item.bonus_eligible" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip small color="success" text-color="white" v-bind="attrs" v-on="on">
                                        <v-icon small left>mdi-gift</v-icon>
                                        Incentivo
                                    </v-chip>
                                </template>
                                <span>30+ rondas con 4.8+ ⭐</span>
                            </v-tooltip>
                            <span v-else class="caption grey--text">-</span>
                        </template>

                        <template v-slot:item.actions="{ item }">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="viewDetails(item)">
                                        <v-icon small>mdi-eye</v-icon>
                                    </v-btn>
                                </template>
                                <span>Ver expediente</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="editItem(item)">
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <span>Editar</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small v-bind="attrs" v-on="on" @click="confirmDelete(item)">
                                        <v-icon small color="red">mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                                <span>Eliminar</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </v-card>
            </v-flex>
        </v-layout>

        <!-- Diálogo para crear/editar caddie (REQ-23) -->
        <v-dialog v-model="dialog" max-width="760px" persistent scrollable>
            <v-card>
                <v-card-title class="primary white--text py-4 px-6">
                    <v-icon left color="white">mdi-account-tie</v-icon>
                    <span class="headline">{{ formTitle }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-6" style="max-height: 72vh;">
                    <v-form ref="form" v-model="validForm" lazy-validation>
                        <!-- Foto y Datos Personales -->
                        <v-row>
                            <v-col cols="12" sm="4" class="text-center">
                                <v-avatar size="120" class="avatar-photo elevation-2 mb-3" :color="photoPreview ? 'transparent' : 'primary'">
                                    <img v-if="photoPreview" :src="photoPreview" alt="Foto">
                                    <span v-else class="white--text display-1 font-weight-bold">
                                        {{ getInitials(editedItem.name) || '?' }}
                                    </span>
                                </v-avatar>
                                <v-file-input
                                    v-model="photoFile"
                                    label="Foto de perfil"
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-camera"
                                    outlined
                                    dense
                                    hide-details
                                    accept="image/*"
                                ></v-file-input>
                            </v-col>

                            <v-col cols="12" sm="8">
                                <div class="subtitle-1 font-weight-bold mb-3 d-flex align-center">
                                    <v-icon left color="primary" small>mdi-card-account-details-outline</v-icon>
                                    Datos Personales
                                </div>
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="editedItem.name"
                                            label="Nombre Completo"
                                            outlined
                                            dense
                                            prepend-inner-icon="mdi-account"
                                            :rules="[v => !!v || 'Nombre es requerido']"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="editedItem.nickname"
                                            label="Apodo/Sobrenombre"
                                            outlined
                                            dense
                                            prepend-inner-icon="mdi-tag-heart-outline"
                                            hint="Campo obligatorio para búsqueda optimizada"
                                            persistent-hint
                                            :rules="[v => !!v || 'Apodo es requerido']"
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                                <v-row dense class="mt-2">
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="editedItem.age"
                                            label="Edad"
                                            type="number"
                                            outlined
                                            dense
                                            hide-details
                                            prepend-inner-icon="mdi-calendar-account"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="editedItem.phone"
                                            label="Teléfono"
                                            outlined
                                            dense
                                            hide-details
                                            prepend-inner-icon="mdi-phone-outline"
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>

                        <v-divider class="my-6"></v-divider>

                        <!-- Documentos INE -->
                        <div class="subtitle-1 font-weight-bold mb-4 d-flex align-center">
                            <v-icon left color="primary" small>mdi-card-account-details</v-icon>
                            Documentos de Identificación (INE)
                        </div>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-card outlined class="pa-3 ine-card">
                                    <div class="caption grey--text font-weight-medium mb-2 text-center text-uppercase">Frente</div>
                                    <div class="ine-preview mb-3" :class="{ 'ine-preview--empty': !inePreviewFront }">
                                        <img v-if="inePreviewFront" :src="inePreviewFront" alt="INE Frente">
                                        <div v-else class="text-center grey--text">
                                            <v-icon size="36" color="grey lighten-1">mdi-image-outline</v-icon>
                                            <div class="caption mt-1">Sin imagen</div>
                                        </div>
                                    </div>
                                    <v-file-input
                                        v-model="inePhotoFront"
                                        prepend-icon=""
                                        prepend-inner-icon="mdi-upload"
                                        :label="inePreviewFront ? 'Cambiar imagen' : 'Subir imagen'"
                                        outlined
                                        dense
                                        hide-details
                                        accept="image/*"
                                    ></v-file-input>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-card outlined class="pa-3 ine-card">
                                    <div class="caption grey--text font-weight-medium mb-2 text-center text-uppercase">Dorso</div>
                                    <div class="ine-preview mb-3" :class="{ 'ine-preview--empty': !inePreviewBack }">
                                        <img v-if="inePreviewBack" :src="inePreviewBack" alt="INE Dorso">
                                        <div v-else class="text-center grey--text">
                                            <v-icon size="36" color="grey lighten-1">mdi-image-outline</v-icon>
                                            <div class="caption mt-1">Sin imagen</div>
                                        </div>
                                    </div>
                                    <v-file-input
                                        v-model="inePhotoBack"
                                        prepend-icon=""
                                        prepend-inner-icon="mdi-upload"
                                        :label="inePreviewBack ? 'Cambiar imagen' : 'Subir imagen'"
                                        outlined
                                        dense
                                        hide-details
                                        accept="image/*"
                                    ></v-file-input>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-divider class="my-6"></v-divider>

                        <!-- Tipo y Estado -->
                        <div class="subtitle-1 font-weight-bold mb-4 d-flex align-center">
                            <v-icon left color="primary" small>mdi-cog-outline</v-icon>
                            Clasificación
                        </div>
                        <v-row align="center">
                            <v-col cols="12" sm="7">
                                <v-select
                                    v-model="editedItem.caddie_type"
                                    :items="caddieTypes"
                                    item-text="text"
                                    item-value="value"
                                    label="Tipo de Caddie"
                                    outlined
                                    dense
                                    hide-details
                                    prepend-inner-icon="mdi-golf"
                                    :rules="[v => !!v || 'Tipo es requerido']"
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="5">
                                <v-switch
                                    v-model="editedItem.is_active"
                                    :label="editedItem.is_active ? 'Activo' : 'Inactivo'"
                                    :color="editedItem.is_active ? 'green' : 'grey'"
                                    inset
                                    hide-details
                                ></v-switch>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDialog">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="save" :disabled="!validForm" :loading="loading">
                        <v-icon left small>mdi-content-save</v-icon>
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo de Expediente Completo -->
        <v-dialog v-model="dialogDetails" max-width="700px">
            <v-card v-if="viewItem">
                <v-card-title>
                    <div class="d-flex align-center">
                        <v-avatar size="48" :color="viewItem.photo ? 'transparent' : 'primary'" class="mr-3">
                            <img v-if="viewItem.photo" :src="viewItem.photo" alt="Foto">
                            <span v-else class="white--text font-weight-bold">{{ getInitials(viewItem.name) }}</span>
                        </v-avatar>
                        <div>
                            <div class="headline">{{ viewItem.name }}</div>
                            <div class="caption grey--text">{{ viewItem.nickname }}</div>
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="dialogDetails = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <!-- Datos Personales -->
                    <div class="subtitle-2 font-weight-bold mb-3">Datos Personales</div>
                    <v-row class="mb-4">
                        <v-col cols="12" sm="6">
                            <div class="caption grey--text">Edad</div>
                            <div class="body-2">{{ viewItem.age }} años</div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="caption grey--text">Teléfono</div>
                            <div class="body-2">{{ viewItem.phone || 'No registrado' }}</div>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <!-- Información de Desempeño (REQ-25, REQ-26) -->
                    <div class="subtitle-2 font-weight-bold mb-3">Desempeño y Calificaciones</div>
                    <v-row class="mb-4">
                        <v-col cols="12" sm="6">
                            <div class="text-center pa-3 grey lighten-4 rounded">
                                <div class="display-2 font-weight-bold primary--text">{{ viewItem.total_rounds_completed }}</div>
                                <div class="caption grey--text">Rondas Completadas</div>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="text-center pa-3 grey lighten-4 rounded">
                                <div class="display-2 font-weight-bold orange--text">
                                    <v-icon color="orange">mdi-star</v-icon>
                                    {{ parseFloat(viewItem.avg_rating_value).toFixed(1) }}
                                </div>
                                <div class="caption grey--text">Calificación Promedio</div>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Alerta de Bono (REQ-26) -->
                    <v-row v-if="viewItem.bonus_eligible" class="mb-4">
                        <v-col cols="12">
                            <v-alert type="success" outlined class="mb-0">
                                <v-icon left>mdi-gift</v-icon>
                                <strong>¡Incentivo Activado!</strong>
                                <div class="caption mt-1">
                                    Caddie ha acumulado {{ viewItem.total_rounds_completed }} rondas con calificación {{ parseFloat(viewItem.avg_rating_value).toFixed(1) }} ⭐
                                </div>
                            </v-alert>
                        </v-col>
                    </v-row>

                    <!-- Detalle de Calificaciones -->
                    <div class="subtitle-2 font-weight-bold mb-3">Últimas Calificaciones</div>
                    <v-list dense>
                        <template v-if="viewItem.recent_ratings && viewItem.recent_ratings.length > 0">
                            <v-list-item v-for="(rating, index) in viewItem.recent_ratings" :key="index">
                                <v-list-item-content>
                                    <div class="caption">{{ formatDate(rating.created_at) }}</div>
                                    <div class="body-2">Ronda con {{ rating.player_name }}</div>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <div class="d-flex align-center">
                                        <v-icon small color="orange">mdi-star</v-icon>
                                        <strong class="ml-1">{{ rating.stars }}</strong>
                                    </div>
                                </v-list-item-action>
                            </v-list-item>
                        </template>
                        <v-list-item v-else>
                            <v-list-item-content>
                                <div class="caption grey--text text-center w-100">Sin calificaciones aún</div>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>

                    <!-- Información Adicional -->
                    <v-divider class="my-4"></v-divider>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <div class="caption grey--text">Tipo de Caddie</div>
                            <v-chip small :color="getTypeColor(viewItem.caddie_type)">
                                {{ getCaddieTypeName(viewItem.caddie_type) }}
                            </v-chip>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="caption grey--text">Estado</div>
                            <v-chip small :color="viewItem.is_active ? 'green' : 'grey'" text-color="white">
                                {{ viewItem.is_active ? 'Activo' : 'Inactivo' }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Diálogo de Confirmación de Eliminar -->
        <v-dialog v-model="dialogDelete" max-width="400px">
            <v-card>
                <v-card-title class="headline">Eliminar Caddie</v-card-title>
                <v-card-text>
                    ¿Estás seguro de que deseas eliminar a <strong>{{ itemToDelete?.name }}</strong>?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDelete">Cancelar</v-btn>
                    <v-btn color="red" text @click="deleteItemConfirm">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
import { caddiesService } from '@/services/caddies'

export default {
  name: 'GolfCaddies',
  data: () => ({
    dialog: false,
    dialogDetails: false,
    dialogDelete: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    loading: false,
    validForm: false,
    searchQuery: '',
    photoFile: null,
    inePhotoFront: null,
    inePhotoBack: null,
    photoPreview: null,
    inePreviewFront: null,
    inePreviewBack: null,
    itemToDelete: null,
    editedIndex: -1,
    viewItem: null,
    headers: [
      { text: 'Foto', value: 'photo', width: '8%', sortable: false },
      { text: 'Nombre / Apodo', value: 'name', width: '25%' },
      { text: 'Tipo', value: 'caddie_type', width: '12%' },
      { text: 'Estado', value: 'is_active', width: '10%' },
      { text: 'Rondas', value: 'total_rounds_completed', width: '10%', align: 'center' },
      { text: 'Calificación', value: 'avg_rating_value', width: '12%', align: 'center' },
      { text: 'Bono', value: 'bonus_eligible', width: '12%', align: 'center' },
      { text: 'Acciones', value: 'actions', width: '15%', sortable: false, align: 'center' }
    ],
    caddieTypes: [
      { text: 'First', value: 1 },
      { text: 'Second', value: 2 },
      { text: 'Bag Carrier', value: 3 }
    ],
    editedItem: {
      id: null,
      name: '',
      nickname: '',
      age: null,
      phone: '',
      photo: null,
      caddie_type: null,
      is_active: true,
      total_rounds_completed: 0,
      avg_rating_value: 0
    },
    defaultItem: {
      id: null,
      name: '',
      nickname: '',
      age: null,
      phone: '',
      photo: null,
      caddie_type: null,
      is_active: true,
      total_rounds_completed: 0,
      avg_rating_value: 0
    },
    caddies: []
  }),
  watch: {
    photoFile (newVal) {
      if (this.photoPreview && this.photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.photoPreview)
      }
      this.photoPreview = newVal ? URL.createObjectURL(newVal) : (this.editedItem.photo || null)
    },
    inePhotoFront (newVal) {
      if (this.inePreviewFront && this.inePreviewFront.startsWith('blob:')) {
        URL.revokeObjectURL(this.inePreviewFront)
      }
      this.inePreviewFront = newVal ? URL.createObjectURL(newVal) : (this.editedItem.ine_front || null)
    },
    inePhotoBack (newVal) {
      if (this.inePreviewBack && this.inePreviewBack.startsWith('blob:')) {
        URL.revokeObjectURL(this.inePreviewBack)
      }
      this.inePreviewBack = newVal ? URL.createObjectURL(newVal) : (this.editedItem.ine_back || null)
    }
  },
  computed: {
    filteredCaddies () {
      if (!this.searchQuery) return this.caddies
      const query = this.searchQuery.toLowerCase()
      return this.caddies.filter(c =>
        c.name.toLowerCase().includes(query) || c.nickname.toLowerCase().includes(query)
      )
    },
    formTitle () {
      return this.editedIndex === -1 ? 'Nuevo Caddie' : 'Editar Caddie'
    },
    activeCaddies () {
      return this.caddies.filter(c => c.is_active).length
    },
    totalRoundsPlayed () {
      return this.caddies.reduce((sum, c) => sum + (c.total_rounds_completed || 0), 0)
    },
    avgRating () {
      const caddiesWithRounds = this.caddies.filter(c => c.total_rounds_completed > 0)
      if (caddiesWithRounds.length === 0) return 0
      const avgSum = caddiesWithRounds.reduce((sum, c) => sum + parseFloat(c.avg_rating_value || 0), 0)
      return (avgSum / caddiesWithRounds.length).toFixed(1)
    }
  },
  methods: {
    async loadCaddies () {
      try {
        this.loading = true
        const response = await caddiesService.getCaddies()
        this.caddies = response.results || response
      } catch (error) {
        console.error('Error loading caddies:', error)
        this.showMessage('Error al cargar caddies', 'error')
      } finally {
        this.loading = false
      }
    },
    getInitials (name) {
      if (!name) return ''
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    },
    getCaddieTypeName (type) {
      const types = {
        1: 'First',
        2: 'Second',
        3: 'Bag Carrier'
      }
      return types[type] || 'Desconocido'
    },
    getTypeColor (type) {
      const colors = {
        1: 'blue',
        2: 'orange',
        3: 'green'
      }
      return colors[type] || 'grey'
    },
    openDialog () {
      this.editedIndex = -1
      this.editedItem = { ...this.defaultItem }
      this.photoFile = null
      this.inePhotoFront = null
      this.inePhotoBack = null
      this.photoPreview = null
      this.inePreviewFront = null
      this.inePreviewBack = null
      this.dialog = true
    },
    editItem (item) {
      this.editedIndex = this.caddies.indexOf(item)
      this.editedItem = { ...item }
      this.photoFile = null
      this.inePhotoFront = null
      this.inePhotoBack = null
      this.photoPreview = item.photo || null
      this.inePreviewFront = item.ine_front || null
      this.inePreviewBack = item.ine_back || null
      this.dialog = true
    },
    async viewDetails (item) {
      try {
        this.loading = true
        const details = await caddiesService.getCaddieDetail(item.id)
        this.viewItem = details
        this.dialogDetails = true
      } catch (error) {
        console.error('Error loading caddie details:', error)
        this.showMessage('Error al cargar detalles del caddie', 'error')
      } finally {
        this.loading = false
      }
    },
    confirmDelete (item) {
      this.itemToDelete = item
      this.dialogDelete = true
    },
    closeDialog () {
      this.dialog = false
      if (this.photoPreview && this.photoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.photoPreview)
      }
      if (this.inePreviewFront && this.inePreviewFront.startsWith('blob:')) {
        URL.revokeObjectURL(this.inePreviewFront)
      }
      if (this.inePreviewBack && this.inePreviewBack.startsWith('blob:')) {
        URL.revokeObjectURL(this.inePreviewBack)
      }
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
        this.photoFile = null
        this.inePhotoFront = null
        this.inePhotoBack = null
        this.photoPreview = null
        this.inePreviewFront = null
        this.inePreviewBack = null
      })
    },
    closeDelete () {
      this.dialogDelete = false
      this.itemToDelete = null
    },
    async save () {
      if (this.$refs.form.validate()) {
        try {
          this.loading = true
          const dataToSend = {
            name: this.editedItem.name,
            nickname: this.editedItem.nickname,
            age: this.editedItem.age,
            phone: this.editedItem.phone,
            caddie_type: this.editedItem.caddie_type,
            is_active: this.editedItem.is_active
          }

          if (this.editedIndex > -1) {
            await caddiesService.updateCaddie(this.editedItem.id, dataToSend)
            this.showMessage('Caddie actualizado correctamente', 'success')
          } else {
            const response = await caddiesService.createCaddie(dataToSend)
            this.caddies.push(response)
            this.showMessage('Caddie agregado correctamente', 'success')
          }
          this.closeDialog()
          await this.loadCaddies()
        } catch (error) {
          console.error('Error saving caddie:', error)
          this.showMessage(
            error.response?.data?.detail || 'Error al guardar caddie',
            'error'
          )
        } finally {
          this.loading = false
        }
      }
    },
    async deleteItemConfirm () {
      try {
        this.loading = true
        await caddiesService.deleteCaddie(this.itemToDelete.id)
        this.showMessage('Caddie eliminado correctamente', 'success')
        this.closeDelete()
        await this.loadCaddies()
      } catch (error) {
        console.error('Error deleting caddie:', error)
        this.showMessage('Error al eliminar caddie', 'error')
      } finally {
        this.loading = false
      }
    },
    showMessage (message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    }
  },
  mounted () {
    this.loadCaddies()
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.avatar-photo img {
  object-fit: cover;
}

.ine-card {
  border-radius: 8px;
}

.ine-preview {
  width: 100%;
  height: 150px;
  border-radius: 6px;
  background-color: #fafafa;
  border: 1px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.ine-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ine-preview--empty {
  border-style: dashed;
}
</style>
